/**
 * ChatMessageList
 * Renders conversation messages with role-based styling, markdown support,
 * streaming indicators, tool activity badges, and smart scroll behaviour
 * (auto-scroll to bottom unless user scrolled up).
 *
 * - User messages: right-aligned, coral gradient
 * - Assistant messages: left-aligned, white card with "M" avatar
 * - System messages: centered, warm tone
 * - Messages > 5000 chars are truncated with a "Show more" toggle
 * - Markdown rendered via react-markdown + remark-gfm
 * - Input sanitized via DOMPurify before rendering
 * - Hover toolbar on assistant messages: Copy, Retry, Thumbs up/down
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import type { Components } from 'react-markdown';
import { Check, ChevronDown, ChevronRight, Copy, RotateCcw, ThumbsUp, ThumbsDown } from 'lucide-react';
import { ArtefactLink } from './ArtefactLink';
import type { TalkMessage, TalkToolCall } from '../../hooks/useTalkSession';

const TRUNCATE_AT = 5_000;

interface ChatMessageListProps {
  messages: TalkMessage[];
  isStreaming: boolean;
  onRetry?: () => void;
}

function sanitize(content: string): string {
  return DOMPurify.sanitize(content, {
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onclick', 'onerror', 'onload', 'onmouseover'],
  });
}

/** Copy button for code blocks — positioned top-right */
function CodeBlockCopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium
                 text-neutral-400 hover:text-neutral-200 bg-neutral-800 hover:bg-neutral-700
                 border border-neutral-700 transition-all duration-150 opacity-0 group-hover/code:opacity-100"
      title="Copy code"
    >
      {copied ? (
        <>
          <Check size={10} />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <Copy size={10} />
          <span>Copy</span>
        </>
      )}
    </button>
  );
}

const markdownComponents: Components = {
  a: ({ href, children }) => <ArtefactLink href={href ?? ''}>{children}</ArtefactLink>,
  h1: ({ children }) => <h1 className="text-base font-bold text-neutral-700 mt-3 mb-1.5">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold text-neutral-700 mt-2.5 mb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-neutral-600 mt-2 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-xs font-semibold text-neutral-600 mt-2 mb-0.5">{children}</h4>,
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 -mx-1 px-1">
      <table className="min-w-0 text-xs border-collapse border border-neutral-200 rounded-lg">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-neutral-50">{children}</thead>,
  th: ({ children }) => (
    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-neutral-600 border border-neutral-200 whitespace-nowrap">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-2 py-1 text-xs text-neutral-700 border border-neutral-200 min-w-0 break-words">{children}</td>
  ),
  img: ({ src, alt }) => <img src={src} alt={alt ?? ''} className="max-w-full rounded-lg my-2" />,
  pre: ({ children }) => {
    // Extract text content from the code element for the copy button
    const codeText = extractTextFromChildren(children);
    return (
      <div className="relative group/code my-2.5">
        <pre className="bg-stone-800 text-stone-100 rounded-xl p-3.5 overflow-x-auto max-w-full text-xs font-mono leading-relaxed border border-stone-700/60">
          {children}
        </pre>
        <CodeBlockCopyButton code={codeText} />
      </div>
    );
  },
  code: ({ children, className }) => {
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return <code className={className}>{children}</code>;
    }
    return (
      <code className="bg-coral-50 text-coral-800 rounded-md px-1.5 py-0.5 text-xs font-mono border border-coral-100 break-all">
        {children}
      </code>
    );
  },
};

/** Recursively extract text content from React children (for code copy) */
function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return extractTextFromChildren((children as React.ReactElement).props.children);
  }
  return '';
}

interface MessageBubbleProps {
  message: TalkMessage;
  isLastAssistant?: boolean;
  onRetry?: () => void;
}

/** Martha "M" avatar — compact warm circle with initial */
function MarthaAvatar() {
  return (
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                 bg-gradient-to-br from-coral-400 to-coral-600 shadow-sm"
    >
      <span className="text-white text-[10px] font-bold leading-none">
        M
      </span>
    </div>
  );
}

function SystemMessage({ message }: MessageBubbleProps) {
  return (
    <div className="flex justify-center my-3 animate-fade-in">
      <div className="max-w-[78%] rounded-xl px-3.5 py-2 bg-sand-100/60 border border-sand-300/40 backdrop-blur-sm">
        <p className="text-xs text-neutral-500 text-center leading-relaxed">{message.content}</p>
      </div>
    </div>
  );
}

function UserMessage({ message }: MessageBubbleProps) {
  return (
    <div className="flex justify-end animate-msg-in-right">
      <div
        className="max-w-[70%] rounded-2xl rounded-br-sm px-3.5 py-2.5
                   bg-gradient-to-br from-coral-500 to-coral-600 text-white
                   shadow-warm-md"
      >
        <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{message.content}</p>
        <span className="text-[10px] mt-1 block text-white/50 text-right">
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

/** Stable color palette for agent badges — deterministic by agent name */
const AGENT_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {};
const PALETTE = [
  { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200/80', dot: 'bg-blue-500' },
  { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200/80', dot: 'bg-violet-500' },
  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200/80', dot: 'bg-emerald-500' },
  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200/80', dot: 'bg-amber-500' },
  { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200/80', dot: 'bg-rose-500' },
  { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200/80', dot: 'bg-cyan-500' },
];
let colorIdx = 0;
function getAgentColor(agent: string) {
  if (!AGENT_COLORS[agent]) {
    AGENT_COLORS[agent] = PALETTE[colorIdx % PALETTE.length];
    colorIdx++;
  }
  return AGENT_COLORS[agent];
}

function AgentBadge({ agent }: { agent: string }) {
  const c = getAgentColor(agent);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${c.bg} ${c.text} ${c.border} border`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {agent}
    </span>
  );
}

function ToolIndicators({ toolCalls }: { toolCalls: TalkToolCall[] }) {
  if (toolCalls.length === 0) return null;

  // Group tool calls by agent (null for non-agent tools)
  const groups: { agent: string | null; calls: TalkToolCall[] }[] = [];
  for (const tc of toolCalls) {
    const agentKey = tc.agent ?? null;
    const last = groups[groups.length - 1];
    if (last && last.agent === agentKey) {
      last.calls.push(tc);
    } else {
      groups.push({ agent: agentKey, calls: [tc] });
    }
  }

  return (
    <div className="flex flex-col gap-1.5 mb-2">
      {groups.map((group, gi) => (
        <div key={gi} className="flex flex-wrap items-center gap-1.5">
          {group.agent && <AgentBadge agent={group.agent} />}
          {group.calls.map((tc, i) => {
            const isRunning = tc.status === 'running';
            const label = tc.displayName ?? tc.name.replace(/_/g, ' ');
            const agentColor = tc.agent ? getAgentColor(tc.agent) : null;
            return (
              <span
                key={`${tc.name}-${gi}-${i}`}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium
                  transition-all duration-300
                  ${isRunning
                    ? tc.agent
                      ? `${agentColor!.bg} ${agentColor!.text} ${agentColor!.border} border shadow-sm`
                      : 'bg-coral-50 text-coral-700 border border-coral-200/80 shadow-sm'
                    : 'bg-neutral-50 text-neutral-500 border border-neutral-200'
                  }`}
                style={isRunning ? {
                  backgroundImage: 'linear-gradient(90deg, transparent 25%, rgba(217,127,111,0.06) 50%, transparent 75%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.8s ease-in-out infinite',
                } : undefined}
              >
                {isRunning ? (
                  <span className="relative flex h-1.5 w-1.5">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${tc.agent ? agentColor!.dot : 'bg-coral-400'}`} />
                    <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${tc.agent ? agentColor!.dot : 'bg-coral-500'}`} />
                  </span>
                ) : (
                  <Check size={10} className={tc.agent ? agentColor!.text : 'text-accent-500'} />
                )}
                {label}
                {tc.status === 'done' && tc.summary && (
                  <span className="text-neutral-400 ml-0.5 truncate max-w-[120px]">{tc.summary}</span>
                )}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Animated "thinking" dots */
function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-1 py-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-coral-400"
          style={{
            animation: 'pulseLive 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </div>
  );
}

/** Agent mode: status strip */
function StatusStrip({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 mb-2 rounded-lg bg-violet-50/60 border border-violet-200/50">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-violet-500" />
      </span>
      <span className="text-[11px] text-violet-600 font-medium">{message}</span>
    </div>
  );
}

/** Agent mode: progress bar */
function ProgressBar({ step, total, description }: { step: number; total: number; description: string }) {
  const pct = total > 0 ? Math.round((step / total) * 100) : 0;
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-neutral-500 font-medium">{description}</span>
        <span className="text-[10px] text-neutral-400 font-mono">{step}/{total}</span>
      </div>
      <div className="h-1 bg-neutral-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-violet-400 to-violet-500 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Agent mode: collapsible thinking section */
function ThinkingSection({ text }: { text: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const preview = text.length > 120 ? text.slice(0, 120) + '...' : text;

  return (
    <div className="mb-2 rounded-lg border border-neutral-200/60 bg-neutral-50/50 overflow-hidden">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-1.5 w-full px-3 py-1.5 text-left hover:bg-neutral-100/50 transition-colors"
      >
        {isOpen ? <ChevronDown size={12} className="text-neutral-400" /> : <ChevronRight size={12} className="text-neutral-400" />}
        <span className="text-[10px] font-medium text-neutral-500">Thinking</span>
        {!isOpen && <span className="text-[10px] text-neutral-400 truncate flex-1 ml-1">{preview}</span>}
      </button>
      {isOpen && (
        <div className="px-3 pb-2 text-[11px] text-neutral-500 leading-relaxed whitespace-pre-wrap font-mono">
          {text}
        </div>
      )}
    </div>
  );
}

/** Hover action toolbar for assistant messages */
function MessageActions({ message, isLastAssistant, onRetry }: {
  message: TalkMessage;
  isLastAssistant?: boolean;
  onRetry?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(message.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [message.content]);

  return (
    <div className="flex items-center gap-0.5 mt-1 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150">
      <button
        onClick={handleCopy}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium
                   text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
        title="Copy message"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        <span>{copied ? 'Copied!' : 'Copy'}</span>
      </button>

      {isLastAssistant && onRetry && (
        <button
          onClick={onRetry}
          className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium
                     text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
          title="Retry response"
        >
          <RotateCcw size={12} />
          <span>Retry</span>
        </button>
      )}

      <button
        onClick={() => setFeedback(feedback === 'up' ? null : 'up')}
        className={`p-1 rounded-md transition-colors ${
          feedback === 'up'
            ? 'text-emerald-600 bg-emerald-50'
            : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'
        }`}
        title="Good response"
      >
        <ThumbsUp size={12} fill={feedback === 'up' ? 'currentColor' : 'none'} />
      </button>

      <button
        onClick={() => setFeedback(feedback === 'down' ? null : 'down')}
        className={`p-1 rounded-md transition-colors ${
          feedback === 'down'
            ? 'text-red-500 bg-red-50'
            : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'
        }`}
        title="Poor response"
      >
        <ThumbsDown size={12} fill={feedback === 'down' ? 'currentColor' : 'none'} />
      </button>
    </div>
  );
}

function AssistantMessage({ message, isLastAssistant, onRetry }: MessageBubbleProps) {
  const [expanded, setExpanded] = useState(false);
  const content = message.content;
  const isTruncatable = content.length > TRUNCATE_AT;
  const displayContent = isTruncatable && !expanded ? content.slice(0, TRUNCATE_AT) : content;
  const sanitized = sanitize(displayContent);
  const hasToolCalls = message.toolCalls && message.toolCalls.length > 0;

  return (
    <div className="flex gap-2 animate-msg-in-left group/msg max-w-[80%]">
      <MarthaAvatar />
      <div className="flex flex-col min-w-0 flex-1">
        <div
          className={`min-w-0 rounded-2xl rounded-tl-sm px-3.5 py-2.5
                     text-neutral-800 border shadow-warm-sm overflow-hidden
                     ${message.agentMode
                       ? 'bg-white border-violet-200/60 ring-1 ring-violet-100/50'
                       : 'bg-white border-neutral-100'
                     }`}
        >
          {message.agentMode && message.isStreaming && (
            <div className="flex items-center gap-1.5 mb-2">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-violet-50 text-violet-600 border border-violet-200/60">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                Agent
              </span>
            </div>
          )}
          {hasToolCalls && <ToolIndicators toolCalls={message.toolCalls!} />}
          {message.statusMessage && <StatusStrip message={message.statusMessage} />}
          {message.progress && (
            <ProgressBar step={message.progress.step} total={message.progress.total} description={message.progress.description} />
          )}
          {message.thinkingText && <ThinkingSection text={message.thinkingText} />}
          <div className="text-[13px] prose prose-sm prose-neutral max-w-none leading-relaxed
                          prose-headings:text-neutral-700 prose-headings:font-semibold
                          prose-p:leading-relaxed prose-li:leading-relaxed prose-li:my-0.5
                          prose-ul:pl-4 prose-ol:pl-4
                          prose-strong:text-neutral-800 prose-strong:font-semibold">
            {content ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {sanitized}
              </ReactMarkdown>
            ) : message.isStreaming ? (
              <ThinkingIndicator />
            ) : null}
            {message.isStreaming && content && (
              <span className="inline-block w-0.5 h-4 bg-coral-500 animate-blink-cursor ml-0.5 align-text-bottom rounded-full" />
            )}
          </div>
          {isTruncatable && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-coral-600 hover:text-coral-800 mt-2 font-medium transition-colors"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
          {/* Agent participation summary */}
          {!message.isStreaming && hasToolCalls && (() => {
            const agents = [...new Set(message.toolCalls!.map((tc) => tc.agent).filter(Boolean))] as string[];
            if (agents.length === 0) return null;
            return (
              <div className="flex items-center gap-1 mt-2 pt-1.5 border-t border-neutral-100">
                <span className="text-[10px] text-neutral-400 mr-0.5">Agents:</span>
                {agents.map((a) => <AgentBadge key={a} agent={a} />)}
              </div>
            );
          })()}
          {!message.isStreaming && content && (
            <span className="text-[10px] mt-2 block text-neutral-300">
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        {/* Action toolbar — appears on hover below the bubble */}
        {!message.isStreaming && content && (
          <MessageActions message={message} isLastAssistant={isLastAssistant} onRetry={onRetry} />
        )}
      </div>
    </div>
  );
}

/** Empty state — shown when there are no messages yet */
function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4
                   bg-gradient-to-br from-coral-400 to-coral-600 shadow-warm-md"
      >
        <span className="text-white text-lg font-bold">M</span>
      </div>
      <p className="text-sm font-medium text-neutral-600 mb-1">Ask Martha anything</p>
      <p className="text-xs text-neutral-400 text-center max-w-[200px]">
        Get help with docs, boards, sprints, code, and more.
      </p>
    </div>
  );
}

/**
 * Renders the list of conversation messages.
 * Auto-scrolls to the bottom when new messages arrive or when streaming,
 * unless the user has manually scrolled up.
 */
export function ChatMessageList({ messages, isStreaming, onRetry }: ChatMessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);

  // Find last assistant message index for retry button
  const lastAssistantIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return i;
    }
    return -1;
  })();

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUpRef.current = distanceFromBottom > 80;
  };

  useEffect(() => {
    if (!userScrolledUpRef.current && containerRef.current) {
      containerRef.current.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, isStreaming]);

  const hasMessages = messages.length > 0;

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-5 py-6"
      style={{
        background: 'linear-gradient(180deg, #FDFCFA 0%, #F9F7F4 100%)',
      }}
    >
      {hasMessages ? (
        <div className="flex flex-col gap-5">
          {messages.map((msg, idx) => {
            if (msg.role === 'system') return <SystemMessage key={msg.id} message={msg} />;
            if (msg.role === 'user') return <UserMessage key={msg.id} message={msg} />;
            return (
              <AssistantMessage
                key={msg.id}
                message={msg}
                isLastAssistant={idx === lastAssistantIdx}
                onRetry={onRetry}
              />
            );
          })}
        </div>
      ) : (
        <EmptyState />
      )}
      <div ref={bottomRef} />
    </div>
  );
}
